/**
 * Sentry noise filters — single source of truth for what should NOT become issues.
 *
 * Used by capture.js, ResponseHandler, axiosInterceptors, and init.js (beforeSend + global handlers).
 * init.js also lists regex fallbacks in `ignoreErrors` for events that bypass app code;
 * when adding a new filter, update shouldReportToSentry here first, then mirror in init.js
 * if the pattern can appear on raw window errors.
 */
import { isAbortError } from '../utils/isAbortError';
import {
  isClientNetworkError,
  isNetworkErrorMessage,
} from '../utils/isClientNetworkError';

function getErrorMessage(err) {
  if (typeof err === 'string') return err;
  if (err instanceof Error) return err.message;
  if (typeof err?.message === 'string') return err.message;
  return '';
}

/**
 * Matrix `/rooms/{roomId}/messages` (and similar) 403 when the caller is no longer
 * a member — typical after the peer leaves or deletes their account and the room
 * has `world_readable` previews disabled (CS API: M_FORBIDDEN + "not in room").
 *
 * Intentionally narrow: do **not** treat every M_FORBIDDEN as expected (send/join
 * permission bugs must still reach Sentry).
 *
 * @param {unknown} err
 * @returns {boolean}
 */
export function isMatrixMembershipForbiddenError(err) {
  const message = getErrorMessage(err).toLowerCase();
  if (!message) return false;

  const membershipHint =
    message.includes('not in room') ||
    message.includes('room previews are disabled');
  if (!membershipHint) return false;

  // Prefer structured MatrixError fields when present.
  const errcode = String(err?.errcode || '').toUpperCase();
  if (errcode) return errcode === 'M_FORBIDDEN';

  const httpStatus = err?.httpStatus ?? err?.statusCode ?? err?.status;
  if (httpStatus != null) return Number(httpStatus) === 403;

  // String-only MatrixError (common from matrix-js-sdk): require forbidden marker.
  return message.includes('m_forbidden') || message.includes('[403]');
}

/**
 * Matrix CS API `M_UNKNOWN_TOKEN` / 401 "Access token has expired".
 * Expected while the client refreshes credentials; not an application bug.
 *
 * Intentionally narrow: do **not** drop every 401 or generic unauthorized error.
 *
 * @param {unknown} err
 * @returns {boolean}
 */
export function isMatrixUnknownTokenError(err) {
  const errcode = String(err?.errcode || err?.data?.errcode || '').toUpperCase();
  if (errcode === 'M_UNKNOWN_TOKEN') return true;

  const message = getErrorMessage(err).toLowerCase();
  if (!message) return false;

  return (
    message.includes('m_unknown_token') ||
    message.includes('access token has expired')
  );
}

/** Matrix SDK errors that are expected during sync, hydration, or stale room state. */
export function isMatrixExpectedError(err) {
  if (isMatrixUnknownTokenError(err) || isMatrixMembershipForbiddenError(err)) {
    return true;
  }
  const message = getErrorMessage(err).toLowerCase();
  if (!message) return false;
  return (
    message.includes('unknown room') ||
    message.includes('m_unknown') ||
    message.includes('no room with id') ||
    message.includes('room not found') ||
    message.includes('hydrate-paginate-timeout')
  );
}

/**
 * React DOM reconciliation failures often caused by browser extensions or
 * translation tools mutating the DOM outside React's control.
 */
export function isDomReconciliationError(err) {
  const message = getErrorMessage(err).toLowerCase();
  if (!message) return false;
  const isNotFound = err?.name === 'NotFoundError' || message.includes('notfounderror');
  return (
    (isNotFound && message.includes('removechild')) ||
    message.includes("failed to execute 'removechild'") ||
    message.includes('the node to be removed is not a child')
  );
}

export function isEmptyErrorValue(err) {
  if (err == null) return true;
  if (typeof err === 'object' && !Array.isArray(err) && !(err instanceof Error)) {
    try {
      return Object.keys(err).length === 0;
    } catch {
      return false;
    }
  }
  return false;
}

/**
 * Single gate for all Sentry capture paths.
 * @param {unknown} err
 * @param {string} [eventMessage]
 */
export function shouldReportToSentry(err, eventMessage = '') {
  if (isEmptyErrorValue(err)) return false;
  if (isAbortError(err)) return false;
  if (isClientNetworkError(err)) return false;
  if (isNetworkErrorMessage(eventMessage)) return false;
  if (isMatrixExpectedError(err) || isMatrixExpectedError({ message: eventMessage })) {
    return false;
  }
  if (
    isDomReconciliationError(err) ||
    isDomReconciliationError({ message: eventMessage })
  ) {
    return false;
  }
  return true;
}

/**
 * HTTP/axios Sentry gate: noise filters plus skip expected 4xx contract errors.
 * Use for axios interceptors; service-layer formatting stays in ResponseHandler.
 */
export function shouldCaptureHttpError(error) {
  if (!shouldReportToSentry(error)) return false;
  const status = error?.response?.status;
  if (status != null && status < 500) return false;
  return true;
}
